import React from 'react';
import purebem from 'purebem';

import toNumber from './utils/toNumber';

import type {
    FoundationTextboxProps,
} from './types';

import './FoundationTextbox.scss';


function noop(): void {}

export default React.forwardRef(function FoundationTextbox({
    autoFocus = false,
    disabled = false,
    hasErrors: errors = false,
    inputMode = null,
    maxLength = void 0,
    onChange = noop,
    placeholder = '',
    symbol = null,
    type = 'text',
    value = '',
}: FoundationTextboxProps, ref) {
    const block = purebem('foundation-textbox');

    const isNumericInput = React.useCallback((): boolean => {
        return [
            'float',
            'float-negative-only',
            'float-positive-only',
            'integer',
            'integer-negative-only',
            'integer-positive-only',
        ].includes(type);
    }, [type]);

    const inputRef = React.useRef<HTMLInputElement>(null);
    React.useImperativeHandle(ref, () => ({
        focus(): void {
            if (inputRef.current !== null) {
                inputRef.current.focus();
            }
        },
        select(): void {
            if (inputRef.current !== null) {
                inputRef.current.select();
            }
        },
    }));

    const handleChange = React.useCallback((evt: React.ChangeEvent<HTMLInputElement>): void => {
        const newText = evt.target.value;

        const {
            isValid,
            value,
        } = isNumericInput()
            ? toNumber(newText, type)
            : { isValid: true, value: newText };

        if (isValid) {
            onChange({ rawValue: newText, value });
        }
    }, [isNumericInput, onChange, type]);

    return (
        <div className={ block({ disabled, errors }) }>
            <input
                autoFocus={ autoFocus }
                className={ block('field') }
                disabled={ disabled }
                inputMode={ inputMode !== null ? inputMode : (isNumericInput() ? 'numeric' : void 0) }
                maxLength={ maxLength }
                onChange={ handleChange }
                placeholder={ placeholder }
                ref={ inputRef }
                type={ type }
                value={ typeof value !== 'string' ? value.toString() : value } />
            <div className={ block('symbol', { disabled, visible: symbol !== null }) }>
                { symbol }
            </div>
        </div>
    );
});
